// openEHR RM model — BMM package org.openehr.rm.data_structures.history
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { T } from "../_shared.ts";
import { LOCATABLE } from "../common/archetyped.ts";
import { DV_DATE_TIME, DV_DURATION } from "../data_types/quantity/date_time.ts";
import type { DV_CODED_TEXT } from "../data_types/text.ts";
import { DATA_STRUCTURE } from "./data_structures.ts";
import type { ITEM_STRUCTURE } from "./item_structure.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Defines the abstract notion of a single event in a series. This class is generic, allowing types to be generated which are locked to particular spatial types, such as \`EVENT<ITEM_LIST>\`. Subtypes express point or intveral data.
 */
export abstract class EVENT<T extends ITEM_STRUCTURE> extends LOCATABLE {
  /**
   * Time of this event. If the width is non-zero, it is the time point of the trailing edge of the event.
   */
  time?: DV_DATE_TIME;
  /**
   * Optional state data for this event.
   */
  state?: ITEM_STRUCTURE;
  /**
   * The data of this event.
   */
  data?: T;
  /**
   * Offset of this event from origin, computed as time.diff(parent.origin).
   * @returns Result value
   */
  offset(): DV_DURATION {
    // Return the difference between event time and parent history origin
    const result = new DV_DURATION();
    
    // Try to get parent history and compute offset
    const parent = this.parent() as HISTORY<T> | undefined;
    if (parent?.origin && this.time) {
      // For now, return a placeholder - full implementation requires
      // date/time arithmetic which depends on temporal polyfill
      const eventTime = this.time.value || "";
      const originTime = parent.origin.value || "";
      
      // Simple implementation: store as ISO 8601 duration string
      // Full implementation would calculate actual difference
      result.value = "PT0S"; // Default to zero duration
    }
    
    return result;
  }
}

/**
 * Defines a single point event in a series.
 */
export class POINT_EVENT<T extends ITEM_STRUCTURE> extends EVENT<T> {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}

/**
 * Defines a single interval event in a series.
 */
export class INTERVAL_EVENT<T extends ITEM_STRUCTURE> extends EVENT<T> {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Duration of the time interval during which the values recorded under \`data\` are true and, if set, the values recorded under \`state\` are true. Void if an instantaneous event.
   */
  width?: DV_DURATION;
  /**
   * Internal storage for sample_count
   * @protected
   */
  protected _sample_count?: openehr_base.Integer;

  /**
   * Optional count of original samples to which this event corresponds.
   */
  get sample_count(): number | undefined {
    return this._sample_count?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for sample_count.
   * Use this to access openehr_base.Integer methods.
   */
  get $sample_count(): openehr_base.Integer | undefined {
    return this._sample_count;
  }

  /**
   * Sets sample_count from either a primitive value or openehr_base.Integer wrapper.
   */
  set sample_count(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._sample_count = undefined;
    } else if (typeof val === "number") {
      this._sample_count = openehr_base.Integer.from(val);
    } else {
      this._sample_count = val;
    }
  }

  /**
   * Mathematical function of the data of this event, e.g.  maximum, mean etc. Coded using https://github.com/openEHR/terminology/blob/master/openEHR_RM/en/openehr_terminology.xml[openEHR vocabulary \`event math function\`]. Default value \`640|actual|\`, meaning 'actual value'.
   */
  math_function?: DV_CODED_TEXT;
  /**
   * Start time of the interval of this event.
   * @returns Result value
   */
  interval_start_time(): DV_DATE_TIME {
    // Start time is the event time minus the width
    const result = new DV_DATE_TIME();
    if (this.time && this.width) {
      // For now, return the time - full implementation would subtract width
      result.value = this.time.value;
    }
    return result;
  }
}

/**
 * Root object of a linear history, i.e. time series structure. This is a generic class whose type parameter must be a descendant of \`ITEM_STRUCTURE\`, ensuring that each Event in the \`_events_\` of a given instance is of the same structural type, i.e. \`ITEM_TREE\`, \`ITEM_LIST\` etc.
 *
 * For a periodic series of events, period will be set, and the time of each Event in the History must correspond; i.e. the \`EVENT._offset_\` must be a multiple of period for each Event. Missing events in a period History are however allowed.
 */
export class HISTORY<T extends ITEM_STRUCTURE> extends DATA_STRUCTURE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Time origin of this event history. The first event is not necessarily at the origin point.
   */
  origin?: DV_DATE_TIME;
  /**
   * Period between samples in this segment if periodic.
   */
  period?: DV_DURATION;
  /**
   * Duration of the entire History; either corresponds to the duration of all the events, and/or the duration represented by the summary, if it exists.
   */
  duration?: DV_DURATION;
  /**
   * Optional summary data that aggregates, organizes, reduces and transforms the event series. This may be a text or image that presents a graphical presentation, or some data that assists with the interpretation of the data.
   */
  summary?: ITEM_STRUCTURE;
  /**
   * The events in the series. This attribute is of a generic type whose parameter must be a descendant of \`ITEM_SUTRUCTURE\`.
   */
  events?: EVENT<T>[];
  /**
   * Indicates whether history is periodic.
   *
   * @returns Result value
   */
  is_periodic(): openehr_base.Boolean {
    // History is periodic if period is set
    return openehr_base.Boolean.from(this.period !== undefined);
  }
}
